
import { useState } from 'react';
import useScrollAnimation from '../../../hooks/useScrollAnimation';

const TestimonialsSection = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const testimonials = [
    {
      id: 1,
      text: 'Tuve un accidente en la C-32 cerca de Gavá y no sabía qué hacer con el coche. Llamé a Desguace Gavá y en menos de 24 horas vinieron a recogerlo y me pagaron al momento. Muy profesionales.',
      name: 'Carlos Martínez García',
      location: 'Gavá, Barcelona',
      avatar: 'https://i.pravatar.cc/150?img=12',
      rating: 5,
      date: 'Hace 2 semanas',
      service: 'Coche siniestrado',
      verified: true,
      platform: 'Google',
    },
    {
      id: 2,
      text: 'Mi coche llevaba meses parado en el garaje sin arrancar. Contacté con ellos por WhatsApp y me dieron un precio justo. Se encargaron de todo el papeleo de la baja.',
      name: 'Laura Sánchez Pérez',
      location: 'Castelldefels, Barcelona',
      avatar: 'https://i.pravatar.cc/150?img=45',
      rating: 5,
      date: 'Hace 1 mes',
      service: 'Coche averiado',
      verified: true,
      platform: 'Google',
    },
    {
      id: 3,
      text: 'Excelente servicio de desguace en Gavá. Necesitaba deshacerme de un coche viejo y ellos me lo compraron sin problemas. Vinieron a Viladecans y me pagaron en efectivo.',
      name: 'Miguel Rodríguez López',
      location: 'Viladecans, Barcelona',
      avatar: 'https://i.pravatar.cc/150?img=33',
      rating: 5,
      date: 'Hace 3 semanas',
      service: 'Coche para chatarra',
      verified: true,
      platform: 'Google',
    },
    {
      id: 4,
      text: 'Después de comparar varios desguaces en el Baix Llobregat, Desguace Gavá fue quien mejor precio me ofreció por mi Seat Ibiza siniestrado. La grúa vino el mismo día.',
      name: 'Ana María Fernández',
      location: 'Sant Boi de Llobregat',
      avatar: 'https://i.pravatar.cc/150?img=26',
      rating: 5,
      date: 'Hace 1 semana',
      service: 'Coche siniestrado',
      verified: true,
      platform: 'Google',
    },
    {
      id: 5,
      text: 'Heredé un coche de mi padre que llevaba años sin ITV ni seguro. Desguace Gavá me explicó todo el proceso y se encargaron de la documentación. Muy agradecido.',
      name: 'Jordi Puig Casals',
      location: 'Gavá Mar, Barcelona',
      avatar: 'https://i.pravatar.cc/150?img=53',
      rating: 5,
      date: 'Hace 2 meses',
      service: 'Gestión de baja',
      verified: true,
      platform: 'Google',
    },
    {
      id: 6,
      text: 'Mi Volkswagen Golf tuvo un fallo de motor y el mecánico me dijo que no valía la pena repararlo. Llamé a Desguace Gavá y en 48 horas tenía el coche vendido.',
      name: 'Patricia Moreno Ruiz',
      location: 'El Prat de Llobregat',
      avatar: 'https://i.pravatar.cc/150?img=44',
      rating: 5,
      date: 'Hace 3 semanas',
      service: 'Coche averiado',
      verified: true,
      platform: 'Google',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState('todos');

  const filters = [
    { id: 'todos', label: 'Todas' },
    { id: 'siniestrado', label: 'Siniestrados' },
    { id: 'averiado', label: 'Averiados' },
    { id: 'chatarra', label: 'Chatarra' },
  ];

  const filteredTestimonials = activeFilter === 'todos' 
    ? testimonials 
    : testimonials.filter(t => t.service.toLowerCase().includes(activeFilter));

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? filteredTestimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === filteredTestimonials.length - 1 ? 0 : prev + 1));
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`ri-star-fill ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
      ></i>
    ));
  };

  const stats = [
    { value: '4.9', label: 'Valoración', icon: 'ri-star-fill', color: 'text-yellow-500' },
    { value: '847', label: 'Reseñas', icon: 'ri-chat-check-fill', color: 'text-emerald-500' },
    { value: '98%', label: 'Satisfechos', icon: 'ri-emotion-happy-fill', color: 'text-blue-500' },
    { value: '15+', label: 'Años', icon: 'ri-award-fill', color: 'text-orange-500' },
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Desguace Gavá",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "847"
    }
  };

  return (
    <section ref={sectionRef} className="py-12 sm:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white" id="testimonios">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div 
          className={`text-center mb-8 sm:mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
            <i className="ri-google-fill"></i>
            <span>Reseñas verificadas de Google</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            Lo Que Dicen Nuestros <span className="text-emerald-600">Clientes</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-4">
            Más de <strong>847 clientes satisfechos</strong> en Gavá y el Baix Llobregat confían en nosotros.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2 sm:gap-4 mb-8 sm:mb-12">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-sm border border-gray-100 text-center hover:shadow-md transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: isVisible ? `${150 + index * 100}ms` : '0ms' }}
            >
              <div className={`w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-1.5 sm:mb-3 rounded-lg sm:rounded-xl bg-gray-50 flex items-center justify-center ${stat.color}`}>
                <i className={`${stat.icon} text-lg sm:text-2xl`}></i>
              </div>
              <p className="text-lg sm:text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-[10px] sm:text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div 
          className={`flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-6 sm:mb-10 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => {
                setActiveFilter(filter.id);
                setCurrentIndex(0);
              }}
              className={`px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeFilter === filter.id
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Main Testimonial Card - Mobile Optimized */}
        <div 
          className={`grid lg:grid-cols-3 gap-4 sm:gap-8 mb-8 sm:mb-12 transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          {/* Featured Testimonial */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10 shadow-xl border border-gray-100 relative overflow-hidden h-full">
              <div className="absolute top-0 right-0 w-24 sm:w-40 h-24 sm:h-40 bg-emerald-50 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              
              <div className="relative">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-6">
                  <div className="flex items-center gap-2">
                    <div className="bg-white border border-gray-200 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 flex items-center space-x-1 sm:space-x-2 shadow-sm">
                      <i className="ri-google-fill text-[#4285F4] text-sm sm:text-base"></i>
                      <span className="text-[10px] sm:text-xs font-medium text-gray-600">Google</span>
                    </div>
                    {filteredTestimonials[currentIndex]?.verified && (
                      <div className="bg-emerald-100 text-emerald-700 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 flex items-center space-x-1">
                        <i className="ri-verified-badge-fill text-xs sm:text-sm"></i>
                        <span className="text-[10px] sm:text-xs font-medium hidden sm:inline">Verificado</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-0.5 sm:space-x-1 text-sm sm:text-base">
                    {renderStars(filteredTestimonials[currentIndex]?.rating || 5)}
                  </div>
                </div>

                <div className="text-3xl sm:text-5xl text-emerald-500 font-serif leading-none mb-2 sm:mb-4">"</div>
                <p className="text-sm sm:text-lg md:text-xl text-gray-800 leading-relaxed mb-6 sm:mb-8">
                  {filteredTestimonials[currentIndex]?.text}
                </p>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <img
                      src={filteredTestimonials[currentIndex]?.avatar}
                      alt={filteredTestimonials[currentIndex]?.name}
                      className="w-10 h-10 sm:w-14 sm:h-14 rounded-full object-cover ring-2 sm:ring-4 ring-emerald-100"
                    />
                    <div>
                      <p className="font-bold text-gray-900 text-sm sm:text-base">{filteredTestimonials[currentIndex]?.name}</p>
                      <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-500">
                        <i className="ri-map-pin-2-fill text-emerald-500"></i>
                        <span>{filteredTestimonials[currentIndex]?.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:flex-col sm:items-end">
                    <span className="inline-block bg-gray-100 text-gray-600 text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full">
                      {filteredTestimonials[currentIndex]?.service}
                    </span>
                    <p className="text-[10px] sm:text-xs text-gray-400 sm:mt-1">{filteredTestimonials[currentIndex]?.date}</p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-100">
                  <p className="text-xs sm:text-sm text-gray-500">
                    <span className="font-semibold text-gray-900">{currentIndex + 1}</span> de {filteredTestimonials.length}
                  </p>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <button
                      onClick={handlePrev}
                      className="w-9 h-9 sm:w-11 sm:h-11 bg-gray-100 hover:bg-gray-200 rounded-lg sm:rounded-xl flex items-center justify-center transition-all cursor-pointer"
                      aria-label="Testimonio anterior"
                    >
                      <i className="ri-arrow-left-s-line text-gray-700 text-lg sm:text-xl"></i>
                    </button>
                    <button
                      onClick={handleNext}
                      className="w-9 h-9 sm:w-11 sm:h-11 bg-emerald-600 hover:bg-emerald-700 rounded-lg sm:rounded-xl flex items-center justify-center transition-all cursor-pointer shadow-lg shadow-emerald-200"
                      aria-label="Siguiente testimonio"
                    >
                      <i className="ri-arrow-right-s-line text-white text-lg sm:text-xl"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Side Stats & CTA */}
          <div className="space-y-4 sm:space-y-6">
            {/* Rating Summary */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <div className="text-4xl sm:text-5xl font-bold text-gray-900">4.9</div>
                <div>
                  <div className="flex items-center space-x-0.5 sm:space-x-1 mb-1 text-sm sm:text-base">
                    {renderStars(5)}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500">847 reseñas en Google</p>
                </div>
              </div>
              
              {/* Rating Bars */}
              <div className="space-y-1.5 sm:space-y-2">
                {[
                  { stars: 5, percent: 92, count: 779 },
                  { stars: 4, percent: 6, count: 51 },
                  { stars: 3, percent: 1, count: 12 },
                  { stars: 2, percent: 0.5, count: 3 },
                  { stars: 1, percent: 0.5, count: 2 },
                ].map((item) => (
                  <div key={item.stars} className="flex items-center space-x-2 sm:space-x-3">
                    <span className="text-xs sm:text-sm text-gray-600 w-3">{item.stars}</span>
                    <i className="ri-star-fill text-yellow-400 text-xs sm:text-sm"></i>
                    <div className="flex-1 h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full"
                        style={{ width: `${item.percent}%` }}
                      ></div>
                    </div>
                    <span className="text-[10px] sm:text-xs text-gray-400 w-6 sm:w-8">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust Badges */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white">
              <h3 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">¿Por qué confiar en nosotros?</h3>
              <ul className="space-y-2 sm:space-y-3">
                {[
                  'Centro Autorizado de Tratamiento (CAT)',
                  '+15 años de experiencia en Gavá',
                  'Certificado de destrucción oficial',
                  'Pago inmediato garantizado',
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-2 sm:space-x-3">
                    <i className="ri-checkbox-circle-fill text-emerald-300 mt-0.5 text-sm sm:text-base"></i>
                    <span className="text-xs sm:text-sm text-emerald-100">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <a
              href="tel:+34932932913"
              className="block bg-gray-900 hover:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white transition-all group cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-400 mb-0.5 sm:mb-1">Llámanos ahora</p>
                  <p className="text-xl sm:text-2xl font-bold">932 932 913</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-phone-fill text-white text-lg sm:text-xl"></i>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Mini Testimonials Grid - Hidden on mobile */}
        <div className="hidden md:grid md:grid-cols-3 gap-4 sm:gap-6">
          {filteredTestimonials.slice(0, 3).map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:shadow-lg transition-all cursor-pointer ${
                index === currentIndex ? 'ring-2 ring-emerald-500 shadow-lg' : ''
              }`}
              onClick={() => setCurrentIndex(index)}
            >
              <div className="flex items-center space-x-1 mb-2 sm:mb-3 text-sm">
                {renderStars(testimonial.rating)}
              </div>
              <p className="text-gray-700 text-xs sm:text-sm line-clamp-3 mb-3 sm:mb-4">
                "{testimonial.text.substring(0, 100)}..."
              </p>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-gray-900 text-xs sm:text-sm">{testimonial.name.split(' ').slice(0, 2).join(' ')}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-xs sm:text-base text-gray-600 mb-3 sm:mb-4">
            ¿Ya has usado nuestro servicio?
          </p>
          <a
            href="https://g.page/r/desguace-gava/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 bg-white border-2 border-gray-200 hover:border-emerald-500 text-gray-700 hover:text-emerald-600 px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all cursor-pointer text-sm"
          >
            <i className="ri-edit-line"></i>
            <span className="whitespace-nowrap">Deja tu reseña en Google</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
